'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';

import type { ProductDisplayData as Product } from '@eliadmin/core/src/api/products/types/product-types';

import { AddToCartButton } from '@eliadmin/core/src/components/product/add-to-cart-button';
import { AddToFavoritesButton } from '@eliadmin/core/src/components/product/add-to-favorites-button';

import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { DeferredMount } from '@eliadmin/core/src/components/deferred/deferred-mount';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import ViewportImage from '../../image/viewport-image';
import { useCategoryStore } from '@eliadmin/core/src/stores/category-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';
import { VariantsProvider, useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import {
  getProductFilterVariants,
  getProductImage,
  getProductUrl,
  isSellable,
} from '@eliadmin/core/src/utils/product-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Link from '@eliadmin/core/src/components/link';
import React, { memo, useContext, useMemo, useState } from 'react';
import { ProductCardHeadingContext } from './heading-context';

import { BiSolidLike } from 'react-icons/bi';
import { FaStar } from 'react-icons/fa';
import { FaCartShopping } from 'react-icons/fa6';
import { FiHeart } from 'react-icons/fi';
import { IoGift } from 'react-icons/io5';
import { CustomAvailabilityLabel } from '../custom-availability-label';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

const AskForPriceDialog = React.lazy(() =>
  import('@eliadmin/core/src/components/product/dialog/ask-for-price-dialog').then(module => ({
    default: module.AskForPriceDialog,
  })),
);

export type ProductCardProps = {
  product: Product;
  imageSizes?: string;
  deferImage?: boolean;
  className?: {
    main?: string;
  };
};

function ProductCardInternal({ product, className, imageSizes, deferImage }: ProductCardProps) {
  const Heading = useContext(ProductCardHeadingContext);
  const CardImage = deferImage ? ViewportImage : Image;
  const { t } = useTranslations();
  const templateSettings = useGlobalStore(s => s.templateSettings);
  const generalLayout = useGlobalStore(s => s.generalLayout);
  const isWholesaler = useCustomerStore(s => s.me?.customer.is_wholesaler);
  const priceDisplay = getProductPriceDisplay(product, {
    isWholesaler,
    priceWithoutVatFirst: templateSettings?.product_detail?.price_without_vat_first?.enabled,
  });
  // The core helper reads attributes only. Keep its complete typed filter contract,
  // but do not rerender every card on page/order/manufacturer filter changes.
  const filter = useCategoryStore(
    state => state.filter,
    (previous, next) => previous.attributes === next.attributes,
  );
  const selectedValues = useVariantsStore(state => state.selectedValues);

  const filterVariants = useMemo(
    () => getProductFilterVariants(product, filter),
    [product, filter],
  );
  const image = useMemo(
    () => getProductImage(templateSettings, product, filterVariants),
    [templateSettings, product, filterVariants],
  );
  const productUrl = useMemo(
    () => getProductUrl(product, selectedValues),
    [product, selectedValues],
  );
  const displayAttributes = useMemo(
    () =>
      [...(product.attributes ?? [])]
        .sort((a, b) => a.attribute_position - b.attribute_position)
        .slice(0, 3),
    [product.attributes],
  );
  const askForPrice = priceDisplay.priceOnRequest;
  const [isOpenAskForPriceDialog, setIsOpenAskForPriceDialog] = useState(false);

  return (
    <div
      className={cn(
        'group transition-all',
        'flex w-full flex-col items-start justify-start gap-3 rounded-[8px] border border-border/50 p-3',
        'ea-product-card__container',
        className?.main,
      )}
    >
      <div className="relative aspect-square w-full">
        <div className="absolute left-1 top-1 z-[2] flex flex-wrap items-start justify-start gap-1 pr-10">
          {!askForPrice && priceDisplay.showPreviousPrice && (
            <div className="flex h-5 items-center justify-center gap-1 rounded-full bg-primary px-2 text-xs font-normal text-white">
              -{priceDisplay.discountLabel}%
            </div>
          )}
          <CustomAvailabilityLabel product={product} />
          {product.tags?.map(tag => (
            <div
              key={tag.name}
              className="flex h-5 items-center justify-center gap-1 rounded-full px-2 text-xs font-normal text-white"
              style={{ backgroundColor: tag.color }}
            >
              {tag.name === 'Doporučujeme' && (
                <BiSolidLike size={10} className="min-h-[10px] min-w-[10px] text-white" />
              )}
              {tag.name === 'Darček' && (
                <IoGift size={10} className="min-h-[10px] min-w-[10px] text-white" />
              )}
              {tag.name}
            </div>
          ))}
        </div>
        {product.reviews_count > 0 && (
          <div
            className={cn(
              'absolute bottom-1 left-1 z-[10] flex items-center justify-start gap-1 rounded-[50px] bg-white px-[6px] py-[3px]',
              'ea-product-card__rating-container',
            )}
          >
            <FaStar size={14} className="text-[#FEBB01]" />
            <p className="text-xs font-normal text-black">
              {product.rating}/5&nbsp;&nbsp;
              <span className="text-xs text-[#838383]">({product.reviews_count})</span>
            </p>
          </div>
        )}
        <div className="absolute right-0 top-0 z-10">
          <DeferredMount
            rootMargin="200px 0px"
            fallback={
              <Button
                type="button"
                variant="outline"
                size="icon-sm"
                className="h-9 w-9 rounded-full border border-border bg-white p-2 shadow-none"
                disabled
                aria-busy="true"
                aria-label={`${t(product.is_favorite ? 'text532' : 'text531')} ${product.name}`}
              >
                <FiHeart
                  aria-hidden="true"
                  className="h-5 w-5"
                />
              </Button>
            }
          >
            <AddToFavoritesButton
              slug={product.slug}
              isFavorite={product.is_favorite}
              productId={product.id}
              showText={false}
              buttonClassName="h-9 w-9 rounded-full border border-border bg-white p-2 shadow-none"
            />
          </DeferredMount>
        </div>
        <Link
          prefetchOnHover
          prefetch={false}
          className="absolute inset-0 z-0 h-full w-full"
          href={productUrl}
          aria-label={product.name}
        >
          <CardImage
            src={image?.url ?? '/images/placeholder.png'}
            alt={resolveImageAlt(image, product?.name)}
            width={0}
            height={0}
            className="absolute inset-0 z-0 h-full w-full object-contain object-center"
            sizes={imageSizes ?? '(min-width: 1280px) 295px, (min-width: 768px) 33vw, 50vw'}
          />
        </Link>
      </div>
      <Link
        prefetchOnHover
        prefetch={false}
        href={productUrl}
        className="flex w-full flex-grow flex-col items-start justify-start gap-2"
      >
        <Heading
          data-ea-visual-tag="h3"
          className="line-clamp-2 h-[32px] text-balance text-left text-xs font-medium text-foreground sm:line-clamp-2 sm:h-10 sm:text-sm"
        >
          {product.name}
        </Heading>
        <div className="flex h-[49px] w-full flex-col items-center justify-center gap-0.5 sm:h-14 sm:gap-1">
          {displayAttributes.map(attribute => (
            <div
              key={attribute.name}
              className="w-full text-center text-[10px] text-muted-foreground sm:text-xs"
            >
              {attribute.name}: {attribute.values?.[0]?.name}
            </div>
          ))}
        </div>
      </Link>
      <div className="flex min-h-[74px] w-full flex-col justify-end overflow-hidden">
        {askForPrice ? (
          <div className="flex items-center justify-start">
            <button
              type="button"
              className="text-left text-sm font-semibold text-primary underline-offset-4 hover:underline"
              onClick={() => setIsOpenAskForPriceDialog(true)}
            >
              {t('text2278')}
            </button>
            {isOpenAskForPriceDialog && (
              <React.Suspense
                fallback={
                  <span role="status" aria-busy="true">
                    Načítava sa…
                  </span>
                }
              >
                <AskForPriceDialog
                  product={product}
                  isOpen={isOpenAskForPriceDialog}
                  setIsOpen={setIsOpenAskForPriceDialog}
                />
              </React.Suspense>
            )}
          </div>
        ) : (
          <>
            <Link
              prefetchOnHover
              prefetch={false}
              href={productUrl}
              className="flex min-h-6 flex-col items-start justify-start gap-1"
            >
              {priceDisplay.showMocVocPrices && priceDisplay.mocPrice != null ? (
                <p className="text-xs text-[#838383]">
                  {t('text1926')}{' '}
                  {formatPrice(
                    priceDisplay.mocPrice,
                    generalLayout.currency,
                    generalLayout.currency_settings,
                  )}
                </p>
              ) : null}
              <div className="flex items-center justify-start gap-3">
                <strong className="text-sm font-medium text-primary">
                  {formatPrice(
                    priceDisplay.current,
                    generalLayout.currency,
                    generalLayout.currency_settings,
                  )}
                </strong>
                {priceDisplay.showPreviousPrice && (
                  <p className="text-sm font-normal text-[#838383] line-through">
                    {formatPrice(
                      priceDisplay.beforeDiscount,
                      generalLayout.currency,
                      generalLayout.currency_settings,
                    )}
                  </p>
                )}
              </div>
              {templateSettings.product_teaser?.price_without_vat?.enabled ? (
                <p className="text-xs text-[#838383]">
                  {priceDisplay.showMocVocPrices ? `${t('text1925')} ` : ''}
                  {formatPrice(
                    priceDisplay.secondary,
                    generalLayout.currency,
                    generalLayout.currency_settings,
                  )}{' '}
                  {priceDisplay.secondaryIncludesVat ? t('text1122') : t('text443')}
                </p>
              ) : null}
            </Link>
            <div
              className={cn(
                'w-full overflow-hidden transition-[max-height,opacity] duration-200 ease-in-out',
                'max-h-[50px] opacity-100',
                'md:max-h-0 md:opacity-0 md:group-hover:max-h-[50px] md:group-hover:opacity-100',
                'pointer-events-auto md:pointer-events-none md:group-hover:pointer-events-auto',
              )}
            >
              <DeferredMount
                rootMargin="200px 0px"
                className="min-h-12 w-full"
                fallback={
                  <Button
                    type="button"
                    className="w-full items-center justify-center space-x-1"
                    disabled
                    aria-busy="true"
                  >
                    {!!templateSettings.product_teaser?.cart_icon && (
                      <FaCartShopping aria-hidden="true" className="text-white" size={20} />
                    )}
                    <span className="line-clamp-1 inline text-ellipsis @[200px]:hidden">
                      {t('text1115')}
                    </span>
                    <span className="line-clamp-1 hidden text-ellipsis @[200px]:inline">
                      {t('text227')}
                    </span>
                  </Button>
                }
              >
                <AddToCartButton
                  product={product}
                  showIcon={!!templateSettings.product_teaser?.cart_icon}
                  sellable={isSellable(product, 1)}
                  className="w-full items-center justify-center"
                  quantity={1}
                />
              </DeferredMount>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export const ProductCard = memo(function ProductCard(props: ProductCardProps) {
  const providerKey = props.product.id
    ? `product-${props.product.id}`
    : `product-${props.product.slug}`;

  return (
    <VariantsProvider
      key={providerKey}
      state={{ product: props.product, selectedQuantity: props.product.min_quantity ?? 1 }}
    >
      <DialogProvider>
        <ProductCardInternal {...props} />
      </DialogProvider>
    </VariantsProvider>
  );
});
